import { Box, makeStyles, TableRow } from '@material-ui/core';
import { LenderName } from '@nemuru-eng/js-api-client';

import { AgentsCountTag } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/AgentsCountTag';
import { LenderAvatars } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/LenderAvatars';
import { OrchestrationConfigCells } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/OrchestrationConfigCells';
import { OrchestrationRow } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OrchestrationRow';
import { Icon } from '@/components/shared/components/Icon';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { TableCellValue } from '@/components/shared/components/TableCellValue';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { StatusTagType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface OrchestrationTableRowProps {
  row: OrchestrationRow;
  lenderNames: LenderName[];
  canEdit: boolean;
  editing: boolean;
  onToggleEdit: () => void;
}

const minHeightCSS = { height: '32px !important' };

const useStyles = makeStyles({
  // useDefaultTable removes the left padding of a row's first cell; sub-rows start at the agents cell.
  subRowFirstCell: { paddingLeft: '8px !important', ...minHeightCSS },
  minHeightCell: { ...minHeightCSS },
  editButton: ({ editing, background }: { editing: boolean; background: string }) => ({
    backgroundColor: editing ? background : undefined,
    minWidth: '86px !important',
    '& .MuiButton-endIcon': {
      transition: 'all 0.3s ease-in-out',
      transform: editing ? 'rotate(180deg)' : 'rotate(0deg)',
    },
  }),
});

export const OrchestrationTableRow = ({
  row,
  lenderNames,
  canEdit,
  editing,
  onToggleEdit,
}: OrchestrationTableRowProps) => {
  const { palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const classes = useStyles({ editing, background: palette.grey[200] });
  const span = row.groups.length;

  return (
    <>
      {row.groups.map((group, index) => (
        <TableRow key={index}>
          {index === 0 && (
            <TableCellValue
              tableCellProps={{ rowSpan: span }}
              value={
                <>
                  <StyledTooltip title={row.paymentMethodType}>
                    <span>
                      <Text strong tId={row.paymentMethodType} />
                    </span>
                  </StyledTooltip>
                  <Box mt={1}>
                    <LenderAvatars lenderNames={lenderNames} />
                  </Box>
                  {row.isMixed && (
                    <StatusTag type={StatusTagType.WARNING} labelTextId="LENDER_ORCHESTRATION_MIXED" mt={1} />
                  )}
                </>
              }
            />
          )}
          <TableCellValue
            tableCellProps={{ className: index > 0 ? classes.subRowFirstCell : classes.minHeightCell }}
            value={<AgentsCountTag agents={group.agents} total={row.totalAgents} mixed={row.isMixed} />}
          />
          <OrchestrationConfigCells paymentMethodType={row.paymentMethodType} config={group.config} />
          {index === 0 && (
            <TableCellValue
              tableCellProps={{ rowSpan: span, align: 'right' }}
              value={
                canEdit ? (
                  <StandardButton
                    variant="text"
                    color="secondary"
                    size="small"
                    aria-expanded={editing}
                    onClick={onToggleEdit}
                    className={classes.editButton}
                    endIcon={<Icon>{'expand_more'}</Icon>}
                  >
                    {getTextIdTranslation(row.isMixed ? 'LENDER_ORCHESTRATION_UNIFY' : 'EDIT')}
                  </StandardButton>
                ) : (
                  ''
                )
              }
            />
          )}
        </TableRow>
      ))}
    </>
  );
};
